// /terms and /privacy pages (hash routes "#terms" / "#privacy") — same
// overlay pattern as the Plans page. The texts live in legalContent.js as
// markdown and are rendered through the chat's safe parser (React text
// nodes only), with page-level typography instead of chat-bubble styles.
import { Fragment, useEffect, useState } from 'react';
import FullPageOverlay from '../home/FullPageOverlay';
import { parseMarkdown } from '../chat/markdown';
import { TERMS_MD, PRIVACY_MD, LEGAL_LAST_UPDATED } from './legalContent';

const PAGES = {
  terms: { title: 'Terms of Service', markdown: TERMS_MD, other: 'privacy' },
  privacy: { title: 'Privacy Policy', markdown: PRIVACY_MD, other: 'terms' },
};

// Above the auth modal (z-index 2000): the sign-up checkbox links here, and
// the page must open on top of the modal, not underneath it.
const LEGAL_OVERLAY_Z_INDEX = 2200;

const HEADING = {
  fontSize: 17,
  fontWeight: 800,
  color: 'var(--text)',
  lineHeight: 1.4,
  margin: '30px 0 10px',
};
const BODY_TEXT = { fontSize: 14, lineHeight: 1.75, color: 'var(--text-2)', margin: '10px 0' };
const LIST = { ...BODY_TEXT, paddingLeft: 22, margin: '10px 0' };
const LIST_ITEM = { margin: '6px 0' };

function InlineText({ tokens }) {
  return tokens.map((t, i) =>
    t.type === 'bold' ? (
      <strong key={i} style={{ color: 'var(--text)' }}>
        {t.content}
      </strong>
    ) : t.type === 'italic' ? (
      <em key={i}>{t.content}</em>
    ) : (
      <Fragment key={i}>{t.content}</Fragment>
    )
  );
}

function LegalBlocks({ markdown }) {
  return parseMarkdown(markdown).map((block, i) => {
    if (block.type === 'heading') {
      return (
        <h2 key={i} style={HEADING}>
          <InlineText tokens={block.inlines} />
        </h2>
      );
    }
    if (block.type === 'ul' || block.type === 'ol') {
      const Tag = block.type;
      return (
        <Tag key={i} style={LIST}>
          {block.items.map((item, j) => (
            <li key={j} style={LIST_ITEM}>
              <InlineText tokens={item} />
            </li>
          ))}
        </Tag>
      );
    }
    // The legal texts only contain headings, lists and paragraphs.
    return (
      <p key={i} style={BODY_TEXT}>
        {block.lines.map((tokens, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            <InlineText tokens={tokens} />
          </Fragment>
        ))}
      </p>
    );
  });
}

export function LegalPage({ page, onBack }) {
  const doc = PAGES[page];
  const other = PAGES[doc.other];
  return (
    <FullPageOverlay label={doc.title} zIndex={LEGAL_OVERLAY_Z_INDEX}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button
            className="btn btn-ghost"
            onClick={onBack}
            style={{ padding: '9px 18px', fontSize: 12 }}
          >
            ← Back
          </button>
        </div>

        <h1
          style={{
            fontSize: 'clamp(24px, 4.5vw, 32px)',
            fontWeight: 800,
            color: 'var(--text)',
            margin: '14px 0 6px',
          }}
        >
          {doc.title}
        </h1>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: 1,
            color: 'var(--text-3)',
            marginBottom: 6,
          }}
        >
          LAST UPDATED: {LEGAL_LAST_UPDATED.toUpperCase()} · BETA VERSION
        </div>
        <a
          href={`#${doc.other}`}
          style={{ fontSize: 12.5, color: 'var(--accent)', textDecoration: 'none' }}
        >
          See also: {other.title} →
        </a>

        <LegalBlocks markdown={doc.markdown} />

        <div
          style={{
            borderTop: '1px solid var(--border-soft)',
            marginTop: 30,
            paddingTop: 16,
            fontSize: 12,
            color: 'var(--text-3)',
            lineHeight: 1.6,
          }}
        >
          Questions about this document? Contact us — the address is in section 1.
        </div>
      </div>
    </FullPageOverlay>
  );
}

// Renders the matching legal page whenever the URL hash is #terms/#privacy
// (footer links, the sign-up checkbox, the Plans page). Same gate pattern
// as PlansGate.
export default function LegalGate() {
  const [hash, setHash] = useState(typeof window !== 'undefined' ? window.location.hash : '');

  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const page = hash === '#terms' ? 'terms' : hash === '#privacy' ? 'privacy' : null;
  if (!page) return null;
  return (
    <LegalPage
      page={page}
      onBack={() => {
        window.location.hash = '';
      }}
    />
  );
}
